import React, { useState } from "react";
import {
	IconChevronDown,
	IconChevronRight,
	IconCircleCheck,
	IconCircleX,
} from "@tabler/icons-react";
import { SimpleSwitch } from "./AsyncSwitch";
import { Divider } from "@mantine/core";

const groupRightsElementOrder = [
	{
		key: "clientsAndMattersSection",
		type: "SECTION",
		header: "Clients + Matters",
		defaultExpanded: true,
		items: [
			{
				key: "clientRights",
				type: "SWITCH",
				label: "Create New Clients",
				stateKey: "CREATE_CLIENT_ALLOWED",
			},
			{
				key: "matterRights",
				type: "SWITCH",
				label: "Create New Matters",
				stateKey: "CREATE_MATTER_ALLOWED",
			},
			{
				key: "manageMatterPermissions",
				type: "SWITCH",
				label: "Manage Matter Permissions",
				stateKey: "UPDATE_MATTER_PERMISSIONS",
			},
			{
				key: "manageDefaultMatterPermissions",
				type: "SWITCH",
				label: "Manage Default Matter Permissions (For New Matters)",
				stateKey: "MANAGE_DEFAULT_MATTER_PERMISSIONS",
			},
		],
	},
	{
		key: "listsAndTagsHeader",
		type: "SECTION",
		header: "Lists + Tags",
		defaultExpanded: true,
		items: [
			{
				key: "manageClientTags",
				type: "SWITCH",
				label: "Add/Edit Client Tags",
				stateKey: "CRUD_CLIENT_TAGS",
			},
			{
				key: "manageMatterTags",
				type: "SWITCH",
				label: "Add/Edit Matter Tags",
				stateKey: "CRUD_MATTER_TAGS",
			},
			{
				key: "manageMatterTypes",
				type: "SWITCH",
				label: "Add/Edit Matter Types",
				stateKey: "CRUD_MATTER_TYPES",
			},
			{
				key: "manageDocumentTags",
				type: "SWITCH",
				label: "Add/Edit Document Tags",
				stateKey: "CRUD_DOCUMENT_TAGS",
			},
			{
				key: "manageDocumentTypes",
				type: "SWITCH",
				label: "Add/Edit Document Types",
				stateKey: "CRUD_DOCUMENT_TYPES",
			},
			{
				key: "manageDocumentStatuses",
				type: "SWITCH",
				label: "Add/Edit Document Statuses",
				stateKey: "CRUD_DOCUMENT_STATUSES",
			},
			{
				key: "officeListManagement",
				type: "SWITCH",
				label: "Add/Edit Locations",
				stateKey: "CRUD_OFFICE_LOCATIONS",
			},
		],
	},
	{
		key: "documentManagementHeader",
		type: "SECTION",
		header: "Document Management",
		defaultExpanded: true,
		items: [
			{
				key: "documentStampingRights",
				type: "SWITCH",
				label: "Manage Document Stamping",
				stateKey: "UPDATE_DOCUMENT_STAMPING_SETTINGS",
			},
			{
				key: "mandatoryProfilingRights",
				type: "SWITCH",
				label: "Manage Mandatory Profiling",
				stateKey: "UPDATE_MANDATORY_PROFILING_SETTINGS",
			},
			{
				key: "ocrProcessingRights",
				type: "SWITCH",
				label: "Can Manually OCR (Request)",
				stateKey: "CAN_REQUEST_OCR_MANUALLY",
			},
			{
				key: "canPermanentDeleteDocuments",
				type: "SWITCH",
				label: "Permanently Delete (Empty Trash Bin)",
				stateKey: "CAN_PERMANENT_DELETE",
			},
			{
				key: "canDeleteDocumentVersions",
				type: "SWITCH",
				label: "Delete Document Versions",
				stateKey: "CAN_DELETE_DOCUMENT_VERSIONS",
			},
			{
				key: "canOverrideDocumentLock",
				type: "SWITCH",
				label: "Override Document Lock",
				stateKey: "CAN_OVERRIDE_DOCUMENT_LOCK",
			},
			{
				key: "archiveMattersRights",
				type: "SWITCH",
				label: "Archive Entire Matters",
				stateKey: "CAN_ARCHIVE_MATTERS",
			},
			{
				key: "archiveMatterContentsRights",
				type: "SWITCH",
				label: "Archive Contents of Matters",
				stateKey: "CAN_ARCHIVE_MATTER_CONTENTS",
			},
		],
	},
	{
		key: "documentSharingHeader",
		type: "SECTION",
		header: "Document Sharing",
		defaultExpanded: true,
		items: [
			{
				key: "canShareDocumentsExternally",
				type: "SWITCH",
				label: "Can Share (Send) Documents",
				stateKey: "CAN_SHARE_DOCUMENTS_EXTERNALLY",
			},
			{
				key: "canSeeAllShares",
				type: "SWITCH",
				label: "See and Manage Shares (all/other users)",
				stateKey: "CAN_SEE_ALL_SHARES",
			},
		],
	},
	{
		key: "integrationManagementHeader",
		type: "SECTION",
		header: "Connections + Integrations",
		defaultExpanded: true,
		items: [
			{
				key: "updateExchangeUrl",
				type: "SWITCH",
				label: "Update Exchange URL",
				stateKey: "UPDATE_EXCHANGE_URL",
			},
			{
				key: "updateIntegrations",
				type: "SWITCH",
				label: "Manage Integrations",
				stateKey: "UPDATE_INTEGRATIONS",
			},
		],
	},
	{
		key: "aiHeader",
		type: "SECTION",
		header: "AI",
		defaultExpanded: true,
		items: [
			{
				key: "updateFirmAiContext",
				type: "SWITCH",
				label: "Manage Firm-wide Custom AI Instructions",
				stateKey: "UPDATE_FIRM_AI_CONTEXT",
			},
		],
	},
];

/**
 * GroupRights component displays and manages group permissions
 * State is managed in parent component, updates via callbacks
 */
const GroupRights = ({
	rights,
	onRightChange,
	isFirmAdminGroup,
	viewingUserCalculatedRights,
	userRightsMessage,
	tenantFeatures,
}) => {
	// Initialize collapsed state based on defaultExpanded property
	const [collapsedSections, setCollapsedSections] = useState(() => {
		const initial = {};
		groupRightsElementOrder.forEach((element) => {
			if (element.type === "SECTION") {
				initial[element.key] = !element.defaultExpanded;
			}
		});
		return initial;
	});

	const handleRightToggle = (rightKey) => {
		onRightChange(rightKey, !rights[rightKey]);
	};

	const toggleSection = (sectionKey) => {
		setCollapsedSections((prev) => ({
			...prev,
			[sectionKey]: !prev[sectionKey],
		}));
	};

	const archiveRightsKeysArray = [
		"CAN_ARCHIVE_MATTERS",
		"CAN_ARCHIVE_MATTER_CONTENTS",
	];

	// archive rights are locked if the archive feature is not enabled at the firm level
	const isArchiveRightLocked = (rightKey) =>
		archiveRightsKeysArray.includes(rightKey) &&
		tenantFeatures.archiveEnabled !== 1;

	const calculateIfSwitchIsDisabled = (rightKey) =>
		isArchiveRightLocked(rightKey) ||
		isFirmAdminGroup ||
		viewingUserCalculatedRights;

	const renderSectionElements = (sectionItem) => {
		switch (sectionItem.type) {
			case "SWITCH":
				if (viewingUserCalculatedRights) {
					// each right will have an object of this format:
					// {
					// 	"hasAccess": false,
					// 	"reason": "not-granted",
					// 	"groups": [
					// 	{
					// 		"groupID": 4,
					// 		"groupName": "Entire Firm",
					// 		"groupType": "ALL_TENANT_USERS"
					// 	}
					// ],
					// 	"isDefault": false
					// }
					// the idea is to display but in text and which groups are granting access if the user has access, and if not, why they don't have access
					const rightInfo = rights[sectionItem.stateKey];
					return (
						<>
							<div
								style={{
									display: "grid",
									gridTemplateColumns: "50px 1fr 2fr",
									alignItems: "center",
									padding: "8px 0",
									gap: "12px",
								}}
							>
								<div style={{ textAlign: "center" }}>
									{rightInfo.hasAccess ? (
										<IconCircleCheck
											style={{
												verticalAlign: "middle",
												color: "var(--lw-info)",
											}}
										/>
									) : (
										<IconCircleX
											style={{
												verticalAlign: "middle",
												color: "var(--lw-darker-red)",
											}}
										/>
									)}
								</div>
								<div>{sectionItem.label}</div>
								<div style={{ fontStyle: "italic" }}>
									{rightInfo.hasAccess ? (
										rightInfo.groups.length > 0 ? (
											<>
												Granted via Group Membership:{" "}
												{rightInfo.groups
													.map(
														(group) =>
															group.groupName
													)
													.join(", ")}
												.
											</>
										) : rightInfo.reason ===
										  "uptimeAdmin" ? (
											"User is an Uptime Admin, has access to all rights"
										) : null
									) : (
										<span style={{ color: "#666" }}>
											{rightInfo.reason === "not-granted"
												? "User does not have this Right."
												: "No access"}
										</span>
									)}
								</div>
							</div>
							<Divider />
						</>
					);
				} else {
					return (
						<>
							<SimpleSwitch
								label={sectionItem.label}
								labelVariant="header-5"
								switchValue={
									rights[sectionItem.stateKey] || false
								}
								handleSwitchValueChange={() =>
									handleRightToggle(sectionItem.stateKey)
								}
								disabled={calculateIfSwitchIsDisabled(
									sectionItem.stateKey
								)}
								disabledHoverText={
									isArchiveRightLocked(sectionItem.stateKey)
										? "Archive is currently not enabled for your firm. Modifying the right is not allowed."
										: ""
								}
							/>
							<Divider />
						</>
					);
				}
			default:
				return null;
		}
	};

	const renderElement = (element) => {
		switch (element.type) {
			case "SELF":
				return element.content;
			case "SECTION": {
				const isCollapsed = collapsedSections[element.key];
				const ChevronIcon = isCollapsed
					? IconChevronRight
					: IconChevronDown;

				return (
					<>
						<h4
							onClick={() => toggleSection(element.key)}
							style={{
								cursor: "pointer",
								display: "flex",
								alignItems: "center",
								userSelect: "none",
							}}
						>
							<ChevronIcon
								size={18}
								style={{ marginRight: "8px" }}
							/>
							{element.header}
						</h4>
						{!isCollapsed && (
							<div
								className="sectionContainer"
								style={{ marginLeft: "26px" }}
							>
								{element.items.map((item) => (
									<div key={item.key}>
										{renderSectionElements(item)}
									</div>
								))}
							</div>
						)}
					</>
				);
			}
			default:
				return null;
		}
	};

	return (
		<div>
			<div>
				{viewingUserCalculatedRights ? (
					<h3>Calculated User Rights</h3>
				) : (
					<h3>Group Rights</h3>
				)}
			</div>
			{isFirmAdminGroup && (
				<div
					style={{
						marginTop: 10,
						padding: 10,
						marginBottom: 10,
						backgroundColor: "#FFF3CD",
						border: "1px solid #FFEEBA",
						borderRadius: 4,
					}}
				>
					Firm Admins is a LexWorkplace system group; it cannot be
					renamed and Rights for this group cannot be modified. Firm
					Admins are granted all available Rights in LexWorkplace.
				</div>
			)}
			<div
				style={{
					backgroundColor: "var(--lw-light-grey-a)",
					padding: 10,
				}}
			>
				{viewingUserCalculatedRights
					? userRightsMessage
					: "Members of this group can..."}
			</div>
			{groupRightsElementOrder.map((element) => (
				<div key={element.key} style={{ marginTop: 15 }}>
					{renderElement(element)}
				</div>
			))}
		</div>
	);
};

export default GroupRights;
